import { LitElement, html } from '@fil-global/webcomponents-base';
import { renderHideLink } from '@fil-global/webcomponents-base/src/template.js';
import { ifDefined, classMap } from '@fil-global/webcomponents-base/src/directives.js';
import { enterSpaceKeys } from '@fil-global/shared/src/util';
import '@fil-global/fil-icon';

import componentStyles from './fil-card.styles.js';

/**
 * @tag fil-card
 * @property {"center"|"left"} [align=center] Defines the alignment of the card content.
 * Options include:
 * - `center`: Centers the content horizontally, name it StandardCard.
 * - `left`: Aligns the content to the left, name it EditorialCard.
 *
 * @property {"primary"|"secondary"} [appearance=primary] Specifies the visual background appearance of the card.
 * Options include:
 * - `primary`: Default appearance with primary styling.
 * - `secondary`: Alternative appearance with secondary styling.
 *
 * @property {string} [icon] Specifies the icon to be displayed in the card.
 * Accepts either:
 * - A predefined icon name (e.g., `home`, `settings`).
 * - A URL to a custom icon image.
 *
 * @property {string} [image] The URL of the image to be displayed in the card.
 * If provided, the image will be rendered as the card's background.
 *
 * @property {string} [url] The URL that the card links to.
 * If set, the card becomes clickable, and clicking on the card or its content will navigate to the specified URL.
 * If not set, the card will not be clickable.
 *
 * @property {string} [target] The target attribute of the link. Default is `_self`.
 */
export class FilCard extends LitElement {
  static properties = {
    align: { type: String },
    appearance: { type: String },
    icon: { type: String },
    image: { type: String },
    url: { type: String },
    target: { type: String },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.align = 'center';
    this.appearance = 'primary';
    this.target = '_self';
  }

  onClick() {
    if (this.url) {
      const hideLink = this.shadowRoot.querySelector('#hideLink');
      hideLink.click();
    }
  }

  handleKeydown(e) {
    if (enterSpaceKeys(e)) {
      this.onClick();
    }
  }

  renderImage() {
    if (this.image) {
      return html`<div
        class="fil-card__image"
        style="background-image:url('${this.image}')"
        @keydown=${this.handleKeydown}
        tabindex="${this.url ? '0' : '-1'}"
      >
        ${renderHideLink(this.url, this.target)}
        <div class="fil-card__image-label"><slot name="label"></slot></div>
      </div>`;
    }

    if (this.icon) {
      if (!this.icon.includes('/')) {
        return html`<fil-icon class="fil-card__icon" icon="${this.icon}" size="64"></fil-icon>`;
      } else {
        return html`<div
          class="fil-card__icon"
          style="background-image:url('${this.icon}')"
          size="64"
        ></div>`;
      }
    }
  }

  slotChangeHandler(event) {
    const slot = event.target;
    const cardContent =
      slot
        ?.assignedElements()
        .filter(node => /^fil-card-content$/.test(node.tagName.toLowerCase())) || [];

    if (cardContent.length > 0 && this.url) {
      cardContent[0].url = this.url;
      cardContent[0].target = this.target;
    }
  }

  render() {
    const classes = {
      [`fil-card--${this.align}`]: this.align !== 'center',
      [`fil-card--${this.appearance}`]: this.appearance !== 'primary',
    };

    return html`<div class="fil-card${classMap(classes)}" ?clickable=${ifDefined(this.url)}>
      ${this.renderImage()}
      <slot @slotchange=${this.slotChangeHandler}></slot>
    </div>`;
  }
}

export default FilCard;
